import fs from "node:fs/promises";
import os from "node:os";
import path from "node:path";
import JSON5 from "json5";
import { defaultConfig, writeConfig, type NoriConfig } from "@mewbleh/nori-core";
import type { AgentStore, NoriPaths } from "@mewbleh/nori-persistence";

export type MigrationOptions = {
  source?: string;
  apply?: boolean;
  includeSessions?: boolean;
  includeCredentials?: boolean;
};

const MAX_TRANSCRIPT_BYTES = 50 * 1024 * 1024;

async function exists(filePath: string): Promise<boolean> {
  try {
    await fs.access(filePath);
    return true;
  } catch {
    return false;
  }
}

async function copyDirectoryMissing(source: string, destination: string): Promise<number> {
  let copied = 0;
  await fs.mkdir(destination, { recursive: true, mode: 0o700 });
  for (const entry of await fs.readdir(source, { withFileTypes: true })) {
    const from = path.join(source, entry.name);
    const to = path.join(destination, entry.name);
    if (entry.isDirectory()) copied += await copyDirectoryMissing(from, to);
    else if (entry.isFile() && !(await exists(to))) {
      await fs.copyFile(from, to, fs.constants.COPYFILE_EXCL);
      copied += 1;
    }
  }
  return copied;
}

function normalizeOpenClawConfig(raw: Record<string, unknown>, paths: NoriPaths): NoriConfig {
  const sanitized = stripInlineSecrets(raw) as Record<string, unknown>;
  const defaults = defaultConfig(paths);
  const config = { ...defaults, ...sanitized } as unknown as NoriConfig;
  config.gateway = { ...defaults.gateway, ...(sanitized.gateway as Partial<NoriConfig["gateway"]> ?? {}) };
  delete (config.gateway as NoriConfig["gateway"] & { controlUi?: unknown }).controlUi;
  config.gateway.mode = "local";
  config.agents = { ...defaults.agents, ...(sanitized.agents as Partial<NoriConfig["agents"]> ?? {}) };
  const importedDefaults = config.agents.defaults as unknown as Record<string, unknown>;
  const importedModel = importedDefaults?.model;
  config.agents.defaults = {
    ...defaults.agents.defaults,
    ...importedDefaults,
    model: typeof importedModel === "string"
      ? { primary: importedModel, fallbacks: [] }
      : { ...defaults.agents.defaults.model, ...(importedModel as Partial<NoriConfig["agents"]["defaults"]["model"]> ?? {}) },
    workspace: paths.workspaceDir,
  };
  config.tools = { ...defaults.tools, profile: "messaging", filesystem: false, shell: false, browser: false, automation: false, externalMessaging: false };
  config.plugins = { allow: [] };
  return config;
}

function stripInlineSecrets(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripInlineSecrets);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(Object.entries(value as Record<string, unknown>).flatMap(([key, entry]) => {
    if (typeof entry === "string" && /(?:api[_-]?key|token|secret|password)$/iu.test(key)) return [];
    return [[key, stripInlineSecrets(entry)]];
  }));
}

function findSecrets(value: unknown, prefix = ""): Array<{ provider: string; profile: string; value: string }> {
  if (!value || typeof value !== "object") return [];
  const output: Array<{ provider: string; profile: string; value: string }> = [];
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (typeof entry === "string" && /(?:api[_-]?key|token|secret)$/iu.test(key) && entry.length > 8) {
      const provider = prefix.split(".").filter(Boolean)[0] ?? "imported";
      output.push({ provider, profile: prefix || "default", value: entry });
    } else if (entry && typeof entry === "object") {
      output.push(...findSecrets(entry, prefix ? `${prefix}.${key}` : key));
    }
  }
  return output;
}

function importedMessage(event: unknown): { role: string; content: unknown } | undefined {
  if (!event || typeof event !== "object") return undefined;
  const record = event as Record<string, unknown>;
  const message = record.type === "message" && record.message && typeof record.message === "object"
    ? record.message as Record<string, unknown>
    : record;
  const role = String(message.role ?? "");
  if (!new Set(["user", "assistant", "tool"]).has(role) || message.content === undefined) return undefined;
  return { role, content: message.content };
}

async function importSessions(
  source: string,
  storeForAgent: (agentId: string) => AgentStore,
): Promise<{ sessions: number; messages: number; skipped: number }> {
  const result = { sessions: 0, messages: 0, skipped: 0 };
  const agentsRoot = path.join(source, "agents");
  let agents: Array<import("node:fs").Dirent>;
  try { agents = await fs.readdir(agentsRoot, { withFileTypes: true }); }
  catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") return result;
    throw error;
  }
  for (const agentEntry of agents) {
    if (!agentEntry.isDirectory() || !/^[a-z0-9][a-z0-9_-]{0,63}$/u.test(agentEntry.name)) {
      result.skipped += 1;
      continue;
    }
    const sessionsDir = path.join(agentsRoot, agentEntry.name, "sessions");
    const indexPath = path.join(sessionsDir, "sessions.json");
    if (!(await exists(indexPath))) continue;
    let index: Record<string, unknown>;
    try { index = JSON5.parse(await fs.readFile(indexPath, "utf8")) as Record<string, unknown>; }
    catch { result.skipped += 1; continue; }
    const canonicalRoot = await fs.realpath(sessionsDir);
    const store = storeForAgent(agentEntry.name);
    for (const [sessionKey, rawEntry] of Object.entries(index)) {
      if (!rawEntry || typeof rawEntry !== "object" || !sessionKey.startsWith("agent:")) {
        result.skipped += 1;
        continue;
      }
      const entry = rawEntry as Record<string, unknown>;
      const sessionId = String(entry.sessionId ?? "");
      const configuredFile = entry.sessionFile ? String(entry.sessionFile) : "";
      const candidate = configuredFile
        ? path.isAbsolute(configuredFile) ? configuredFile : path.resolve(sessionsDir, configuredFile)
        : path.join(sessionsDir, `${sessionId}.jsonl`);
      let transcriptPath: string;
      try { transcriptPath = await fs.realpath(candidate); }
      catch { result.skipped += 1; continue; }
      if (transcriptPath !== canonicalRoot && !transcriptPath.startsWith(`${canonicalRoot}${path.sep}`)) {
        result.skipped += 1;
        continue;
      }
      const stat = await fs.stat(transcriptPath);
      if (!stat.isFile() || stat.size > MAX_TRANSCRIPT_BYTES) {
        result.skipped += 1;
        continue;
      }
      const session = store.ensureSession({ sessionKey, agentId: agentEntry.name });
      if (store.history(session.sessionId, 1).length) {
        result.skipped += 1;
        continue;
      }
      const lines = (await fs.readFile(transcriptPath, "utf8")).split(/\r?\n/u);
      let imported = 0;
      for (const line of lines) {
        if (!line.trim()) continue;
        try {
          const message = importedMessage(JSON.parse(line) as unknown);
          if (!message) continue;
          store.appendTranscript(session.sessionId, message.role, message.content);
          imported += 1;
        } catch {
          result.skipped += 1;
        }
      }
      result.sessions += 1;
      result.messages += imported;
    }
  }
  return result;
}

export async function migrateOpenClaw(
  paths: NoriPaths,
  agentStore: AgentStore,
  options: MigrationOptions,
  storeForAgent: (agentId: string) => AgentStore = () => agentStore,
) {
  const source = path.resolve(options.source ?? path.join(os.homedir(), ".openclaw"));
  const sourceConfig = path.join(source, "openclaw.json");
  const sourceWorkspace = path.join(source, "workspace");
  const credentialCandidates = [
    path.join(source, "auth-profiles.json"),
    path.join(source, "auth-state.json"),
    path.join(source, "agents", "main", "agent", "auth-profiles.json"),
  ];
  const plan = {
    source,
    apply: Boolean(options.apply),
    config: await exists(sourceConfig),
    workspace: await exists(sourceWorkspace),
    credentials: options.includeCredentials ? (await Promise.all(credentialCandidates.map(exists))).filter(Boolean).length : 0,
    sessionsRequested: Boolean(options.includeSessions),
    notes: [],
    copiedFiles: 0,
    importedSecrets: 0,
    importedSessions: 0,
    importedMessages: 0,
    skippedSessionEntries: 0,
  };
  if (!options.apply) return plan;
  const backupDir = path.join(paths.stateDir, "migrations", `openclaw-${Date.now()}`);
  await fs.mkdir(backupDir, { recursive: true, mode: 0o700 });
  if (plan.config) {
    await fs.copyFile(sourceConfig, path.join(backupDir, "openclaw.json"));
    const raw = JSON5.parse(await fs.readFile(sourceConfig, "utf8")) as Record<string, unknown>;
    await writeConfig(paths, normalizeOpenClawConfig(raw, paths));
  }
  if (plan.workspace) plan.copiedFiles = await copyDirectoryMissing(sourceWorkspace, paths.workspaceDir);
  if (options.includeCredentials) {
    for (const candidate of credentialCandidates) {
      if (!(await exists(candidate))) continue;
      const raw = JSON5.parse(await fs.readFile(candidate, "utf8")) as unknown;
      for (const secret of findSecrets(raw)) {
        agentStore.setCredential(secret.provider, secret.profile, secret.value);
        plan.importedSecrets += 1;
      }
    }
  }
  if (options.includeSessions) {
    const imported = await importSessions(source, storeForAgent);
    plan.importedSessions = imported.sessions;
    plan.importedMessages = imported.messages;
    plan.skippedSessionEntries = imported.skipped;
  }
  return plan;
}
